import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearClassLinkState,
  getClassLinkRedirectUri,
  readClassLinkState,
} from "../../util/classLinkSSOConfig";
import { classLinkSSOLogin } from "../../sagas/actions";
import { fetchFreshPixelToken } from "../../util/pixelToken";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  padding: 24px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.92);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  padding: 32px 40px;
  max-width: 420px;
  width: 100%;
  text-align: center;

  @media (max-width: 480px) {
    padding: 24px;
  }
`;

const Title = styled.h2`
  margin: 0 0 12px;
  color: #0c2b3f;
  font-size: 20px;
`;

const Status = styled.p`
  margin: 12px 0;
  color: #1d3e56;
  font-size: 15px;
  line-height: 1.5;
`;

const ErrorBox = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fdecef;
  color: #8b1e2f;
  border: 1px solid #f5c0c9;
  font-size: 14px;
  line-height: 1.5;
`;

const RetryLink = styled(Link)`
  display: inline-block;
  margin-top: 20px;
  padding: 10px 16px;
  background: #195174;
  color: #ffffff;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.15s ease;

  &:hover {
    background: #1f628d;
  }
`;

const parseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    code: params.get("code"),
    state: params.get("state"),
    error: params.get("error"),
    errorDescription: params.get("error_description"),
  };
};

const shouldSkipDispatch = (status) =>
  ["error", "complete"].includes(status);

const ClassLinkCallbackPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const signedIn = useSelector((state) => state.auth.signedIn);
  const [status, setStatus] = useState("Processing your ClassLink sign-in…");
  const [errorMessage, setErrorMessage] = useState("");
  const [attemptedDispatch, setAttemptedDispatch] = useState(false);

  const { code, state, error, errorDescription } = useMemo(
    () => parseQueryParams(location.search),
    [location.search]
  );

  useEffect(() => {
    if (signedIn) {
      navigate("/myinfo", { replace: true });
    }
  }, [signedIn, navigate]);

  useEffect(() => {
    if (shouldSkipDispatch(status)) {
      return;
    }

    if (attemptedDispatch) {
      return;
    }

    if (error) {
      const message =
        errorDescription ||
        (error === "access_denied"
          ? "ClassLink sign-in was cancelled. Please try again."
          : "We could not complete your ClassLink sign-in.");

      setStatus("Unable to finish ClassLink sign-in.");
      setErrorMessage(message);
      clearClassLinkState();
      setAttemptedDispatch(true);
      return;
    }

    if (!code) {
      const message =
        "Missing authorization code. Please start the ClassLink sign-in again.";
      setStatus("Unable to finish ClassLink sign-in.");
      setErrorMessage(message);
      clearClassLinkState();
      setAttemptedDispatch(true);
      return;
    }

    const expectedState = readClassLinkState();
    if (!state || !expectedState || state !== expectedState) {
      const message = "Your sign-in session expired. Please try again.";
      setStatus("Unable to finish ClassLink sign-in.");
      setErrorMessage(message);
      clearClassLinkState();
      setAttemptedDispatch(true);
      return;
    }

    const finalize = async () => {
      try {
        setStatus("Securing your sign-in…");
        const pixelToken = await fetchFreshPixelToken();
        if (!pixelToken) {
          throw new Error("missing_pixel_token");
        }

        dispatch(
          classLinkSSOLogin({
            code,
            redirectUri: getClassLinkRedirectUri(),
            pixelToken,
          })
        );
        setStatus("Signing you in…");
      } catch (err) {
        console.error("ClassLink SSO completion error", err);
        const message =
          err?.message === "missing_pixel_token"
            ? "Unable to secure the sign-in request. Please refresh and try again."
            : "We hit a snag while finishing ClassLink sign-in. Please try again.";
        setStatus("Unable to finish ClassLink sign-in.");
        setErrorMessage(message);
      } finally {
        clearClassLinkState();
        setAttemptedDispatch(true);
      }
    };

    finalize();
  }, [
    attemptedDispatch,
    code,
    dispatch,
    error,
    errorDescription,
    state,
    status,
  ]);

  return (
    <Container>
      <Card>
        <Title>ClassLink Sign-In</Title>
        <Status>{status}</Status>
        {errorMessage && (
          <>
            <ErrorBox>{errorMessage}</ErrorBox>
            <RetryLink to="/user/login">Back to Login</RetryLink>
          </>
        )}
      </Card>
    </Container>
  );
};

export default ClassLinkCallbackPage;
